import { useEffect, useState } from 'react';
import './style.scss';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { TranslationService } from '../../../../services/translation.service';
import { type ITravelInformationState } from '../TravelInformation';
import { type ICarInformationState } from '../CarInformation';
import { PublicTransportCalculator } from '../../helpers/public-transport.calculator';
import { VariableCarCostsCalculator } from '../../helpers/variable-car-costs.calculator';
import { TotalCarCostsCalculator } from '../../helpers/total-car-cost.calculator';
import { PublicTransportConstants } from '../../constants/public-transport.constants';
import ResultTextHelper from '../../helpers/result-text-helper';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { TicketHelper } from '../../helpers/ticket.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import Colors from '../../../../helpers/colors.helper';

function createDiagonalPattern(primaryColor = 'black', secondaryColor = 'white') {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const size = 13;
  const stroke = 5;
  const strokeOffset = stroke / 2;
  canvas.width = size;
  canvas.height = size;

  if (!context) {
    return '';
  }

  context.fillStyle = secondaryColor;
  context.fillRect(0, 0, size, size);

  context.strokeStyle = primaryColor;
  context.globalAlpha = 0.1;
  context.lineWidth = stroke;
  context.lineCap = 'round';

  context.beginPath();
  context.moveTo(size / 2 - strokeOffset, -strokeOffset);
  context.lineTo(size + strokeOffset, size / 2 + strokeOffset);
  context.moveTo(-strokeOffset, size / 2 - strokeOffset);
  context.lineTo(size / 2 + strokeOffset, size + strokeOffset);
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

function ResultWithGraph({
  travelInformation,
  carInformation,
  updateResult,
  resetUpdateResult,
  usedDistanceInCalculationValueChanged,
}: {
  travelInformation: ITravelInformationState;
  carInformation: ICarInformationState;
  updateResult: boolean;
  resetUpdateResult: () => void;
  usedDistanceInCalculationValueChanged?: (value: number) => void;
}) {
  const [totalCostPublicTransportState, setTotalCostPublicTransportState] = useState(0);
  const [totalCarCostState, setTotalCarCostState] = useState(0);
  const [variableCarCostState, setVariableCarCostState] = useState(0);
  const [cheapestAlternativeText, setCheapestAlternativeText] = useState('');
  const [cheapestHeadingText, setCheapestHeadingText] = useState('');

  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: (string | CanvasPattern)[];
    }[];
  }>({
    labels: ['', '', ''],
    datasets: [
      {
        label: 'Costs',
        data: [],
        backgroundColor: [Colors.vtdGray900, Colors.vtdGray900, createDiagonalPattern(Colors.vtdBlue800, Colors.vtdBlue100) || Colors.vtdBlue800],
      },
    ],
  });

  const options = {
    responsive: true,
    scales: {
      x: {
        grid: { display: false },
        ticks: { display: false },
      },
      y: { display: false },
    },
    layout: { padding: { top: window.innerWidth <= 768 ? 30 : 50 } },
    hover: { mode: undefined },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        align: 'top' as const,
        anchor: 'end' as const,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        color: (context: any) => {
          const index = context.dataIndex;
          const colors = [Colors.vtdGray900, Colors.vtdGray900, Colors.vtdGray800];
          return colors[index];
        },
        font: {
          size: window.innerWidth <= 768 ? 20 : (28 as const),
          weight: 'bold' as const,
        },
        formatter: (value: number) => `${value} kr`,
      },
    },
  };

  function createAriaLiveText(publicTransportCost: number, variableCarCost: number, totalCarCost: number): string {
    return `${TranslationService.get('pricecalculatorpage.resultheading')} ${TranslationService.get('pricecalculatorpage.result.ariagraphdescription')
      .replace('#publictransport#', publicTransportCost.toString())
      .replace('#carcommutingcost#', variableCarCost.toString())
      .replace('#totalcarcost#', totalCarCost.toString())}`;
  }

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  useEffect(() => {
    function getCheapestHeadingText(publicTransportCost: number, carCost: number) {
      if (publicTransportCost <= carCost) {
        return TranslationService.get('pricecalculatorpage.commutebypublictransportischeapestheadingtext').replace('{cheapest-alternative-cost}', `${(carCost - publicTransportCost).toString()}`);
      }
      return TranslationService.get('pricecalculatorpage.commutebycarischeapestheadingtext').replace('{cheapest-alternative-cost}', `${(publicTransportCost - carCost).toString()}`);
    }

    function getCheapestAlternativeText(publicTransportCost: number, carCost: number): string {
      if (publicTransportCost <= carCost) {
        const { selectedTicketCost } = ResultTextHelper.getSelectedTicketAndCost(travelInformation.value.prefferredTicketType, travelInformation.value.travelZones);
        return TranslationService.get('pricecalculatorpage.commutebypublictransportischeapestalternativetext')
          .replace(
            '{selected-ticket}',
            TicketHelper.getSelectedTicketWithNumberOfZonesText(travelInformation.value.prefferredTicketType, travelInformation.value.travelZones, (x) => TranslationService.get(x))
          )
          .replace('{selected-ticket-cost}', `${selectedTicketCost}`);
      }
      return TranslationService.get('pricecalculatorpage.commutebycarischeapestalternativetext').replace('{cheapest-alternative-cost}', `${(publicTransportCost - carCost).toString()}`);
    }

    function updateResultCalculations() {
      // Public transport
      const publicTransportCost = PublicTransportCalculator.calculateCost(
        {
          numberOfTravelDaysPerMonth: travelInformation.value.travelDaysPerMonth || 0,
          zonesToTravelWithin: travelInformation.value.travelZones,
          preferredTicketType: travelInformation.value.prefferredTicketType,
        },
        PublicTransportConstants
      );
      setTotalCostPublicTransportState(publicTransportCost.costInSek);

      // Variable car costs
      const variableCarCost = VariableCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value);
      setVariableCarCostState(variableCarCost.costPerMonth);

      // Fixed car costs
      const totalCarCost = TotalCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value);
      setTotalCarCostState(totalCarCost.totalCost);

      if (totalCarCost.yearlyCommuteGreatherThanTotalDistance && usedDistanceInCalculationValueChanged) {
        usedDistanceInCalculationValueChanged(totalCarCost.yearlyCommuteKm);
      }

      // Update cheapest heading text
      setCheapestHeadingText(getCheapestHeadingText(publicTransportCost.costInSek, variableCarCost.costPerMonth));
      // Update cheapest alternative text
      setCheapestAlternativeText(getCheapestAlternativeText(publicTransportCost.costInSek, variableCarCost.costPerMonth));

      // ariaLiveText.current = createAriaLiveText(totalCostPublicTransportState, variableCarCostState, totalCarCostState);

      // Track the result
      let trackingLabel: string = '';
      if (Math.round(publicTransportCost.costInSek) === Math.round(variableCarCost.costPerMonth)) trackingLabel = 'Samma';
      else if (publicTransportCost.costInSek <= variableCarCost.costPerMonth) trackingLabel = 'Kollektivtrafik';
      else trackingLabel = 'Bilpendling';
      AnalyticsService.addEvent('PriceCalculator', 'Resultat uppdaterat', trackingLabel);
    }

    if (updateResult) {
      updateResultCalculations();
      resetUpdateResult();
    }
  }, [updateResult, resetUpdateResult, travelInformation, carInformation, usedDistanceInCalculationValueChanged]);

  useEffect(() => {
    setChartData((prevChartData) => {
      const newChartData = { ...prevChartData };
      newChartData.datasets = prevChartData.datasets.map((dataset) => {
        const newDataset = { ...dataset };
        newDataset.data = [totalCostPublicTransportState, variableCarCostState, totalCarCostState];
        return newDataset;
      });
      return newChartData;
    });
  }, [totalCostPublicTransportState, variableCarCostState, totalCarCostState]);

  return (
    <div className="result-with-graph">
      <div className="container mb-4 mb-md-5">
        <div className="container container--narrower text-center" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(cheapestHeadingText) }}></div>
        <Bar options={options} data={chartData} />
        <div id="graph-description" className="visually-hidden" aria-live="polite">
          {createAriaLiveText(totalCostPublicTransportState, variableCarCostState, totalCarCostState)}
        </div>
        <div className="d-flex">
          <div className="result-with-graph__x-axis-label text-center">{TranslationService.get('pricecalculatorpage.result.publictransportchart')}</div>
          <div className="result-with-graph__x-axis-label text-center">{TranslationService.get('pricecalculatorpage.result.variablecarcostchart')}</div>
          <div className="result-with-graph__x-axis-label result-with-graph__light text-center">{TranslationService.get('pricecalculatorpage.result.fixedcarcostchart')}</div>
        </div>
      </div>

      <div className="container container--narrower">
        <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(cheapestAlternativeText) }}></div>
      </div>
    </div>
  );
}

export default ResultWithGraph;
